import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import type { CalendarEventInstance } from '@/lib/api';
import type { TimeFormat } from '../types';
import { formatTime, formatDateKey } from '../utils';
import { getEventColor } from '../utils';

interface SearchBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  results: CalendarEventInstance[];
  loading: boolean;
  onSelectEvent: (event: CalendarEventInstance) => void;
  onDeleteEvents?: (eventIds: string[]) => Promise<void>;
  onClear: () => void;
  timeFormat?: TimeFormat;
}

export function SearchBar({
  query,
  onQueryChange,
  results,
  loading,
  onSelectEvent,
  onDeleteEvents,
  onClear,
  timeFormat = '12h',
}: SearchBarProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const hasResults = results.length > 0;
  const searched = query.trim().length >= 2;
  const showPopover = open && searched;
  const selectionMode = selectedIds.size > 0;

  useEffect(() => {
    setActiveIndex(-1);
  }, [results]);

  // Clear selection when results change
  useEffect(() => {
    setSelectedIds(new Set());
    setLastClickedIndex(null);
  }, [results]);

  const toggleSelection = useCallback(
    (index: number, shiftKey: boolean) => {
      const eventId = results[index]?.eventId;
      if (!eventId) return;

      setSelectedIds((prev) => {
        const next = new Set(prev);

        if (shiftKey && lastClickedIndex !== null) {
          const from = Math.min(lastClickedIndex, index);
          const to = Math.max(lastClickedIndex, index);
          for (let i = from; i <= to; i++) {
            next.add(results[i].eventId);
          }
        } else {
          if (next.has(eventId)) {
            next.delete(eventId);
          } else {
            next.add(eventId);
          }
        }

        return next;
      });

      setLastClickedIndex(index);
    },
    [results, lastClickedIndex],
  );

  const handleDelete = useCallback(async () => {
    if (!onDeleteEvents || selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await onDeleteEvents(Array.from(selectedIds));
      setSelectedIds(new Set());
    } finally {
      setDeleting(false);
    }
  }, [onDeleteEvents, selectedIds]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showPopover) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < results.length) {
            if (selectionMode) {
              toggleSelection(activeIndex, e.shiftKey);
            } else {
              onSelectEvent(results[activeIndex]);
              setOpen(false);
              onClear();
            }
          }
          break;
        case 'Escape':
          if (selectionMode) {
            setSelectedIds(new Set());
          } else {
            setOpen(false);
            onClear();
            inputRef.current?.blur();
          }
          break;
      }
    },
    [showPopover, activeIndex, results, selectionMode, onSelectEvent, onClear, toggleSelection],
  );

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const formatEventDate = (event: CalendarEventInstance) => {
    const start = new Date(event.startAt);
    const dateStr = start.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    if (event.isAllDay) return dateStr;
    return `${dateStr} ${formatTime(start, timeFormat)}`;
  };

  return (
    <Popover open={showPopover} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              onQueryChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search events..."
            className="h-8 w-48 pl-7 pr-7 text-xs"
          />
          {query && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={() => {
                onClear();
                setSelectedIds(new Set());
                setOpen(false);
              }}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
          )}
        </div>
      </PopoverAnchor>

      <PopoverContent
        className="w-80 p-0 max-h-72 overflow-y-auto"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && hasResults && (
          <>
            <div ref={listRef}>
              {results.map((event, i) => {
                const isSelected = selectedIds.has(event.eventId);
                return (
                  <div
                    key={`${event.eventId}-${formatDateKey(new Date(event.startAt))}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-start gap-2 border-b last:border-b-0 ${
                      i === activeIndex ? 'bg-muted/50' : ''
                    }`}
                    onMouseEnter={() => setActiveIndex(i)}
                  >
                    {onDeleteEvents && (
                      <button
                        type="button"
                        className="mt-0.5 shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSelection(i, e.shiftKey);
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          tabIndex={-1}
                          className="pointer-events-none"
                          aria-label={`Select ${event.title}`}
                        />
                      </button>
                    )}
                    <button
                      type="button"
                      className="flex items-start gap-2 flex-1 min-w-0"
                      onClick={(e) => {
                        if (selectionMode) {
                          toggleSelection(i, e.shiftKey);
                        } else {
                          onSelectEvent(event);
                          setOpen(false);
                          onClear();
                        }
                      }}
                    >
                      <div
                        className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                        style={{ backgroundColor: getEventColor(event) }}
                      />
                      <div className="min-w-0 flex-1 text-left">
                        <div className="font-medium truncate">{event.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatEventDate(event)}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
            {selectionMode && (
              <div className="sticky bottom-0 border-t bg-background px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={deleting}
                  onClick={handleDelete}
                  className="h-7 text-xs"
                >
                  {deleting ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3 mr-1" />
                  )}
                  Delete {selectedIds.size}
                </Button>
              </div>
            )}
          </>
        )}
        {!loading && !hasResults && query.trim().length >= 2 && (
          <div className="py-4 text-center text-sm text-muted-foreground">No events found</div>
        )}
      </PopoverContent>
    </Popover>
  );
}
